/**
 * "Save these photos to my phone."
 *
 * The honest version of a feature that is usually oversold. No web API can
 * write to the camera roll, so what this does depends entirely on the platform,
 * and the copy says so rather than implying a one-tap save:
 *
 *  - **iOS** → the share sheet, in batches. The user still taps "Save Image"
 *    there; we cannot do it for them and cannot detect whether they did.
 *  - **Android** → ordinary downloads. They land in the Downloads folder and
 *    the gallery picks them up from there, which is a real outcome worth
 *    stating plainly — but they are not added to the main Google Photos feed.
 *  - **Anything else on iOS** → instructions for the long-press menu, which is
 *    the only route left.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatBytes } from "@fc/core";
import { fetchSignedUrls } from "../../lib/signedUrls.js";
import { Button } from "../../components/ui/Button.js";
import { Dialog } from "../../components/ui/Dialog.js";
import { ProgressBar } from "../../components/ui/ProgressBar.js";
import { useToast } from "../../components/ui/Toast.js";
import { imageAccept } from "../../lib/imageSupport.js";
import { acquireDecryptedUrl, release } from "../../lib/mediaCache.js";
import {
  batchForSharing,
  downloadFiles,
  fetchAsFiles,
  isApplePhone,
  saveStrategy,
  shareFiles,
  SHARE_MAX_FILES,
  type DownloadableFile,
} from "../../lib/saveToPhone.js";
import "./SaveToPhone.css";

export interface SaveTarget {
  id: string;
  name: string;
  sizeBytes: number;
}

export interface SaveToPhoneProps {
  groupId: string;
  albumName: string;
  items: SaveTarget[];
  /** Originals when the group allows them; the large preview otherwise. */
  canUseOriginals: boolean;
  onClose: () => void;
}

type Phase = "idle" | "working" | "done";

export function SaveToPhone({
  groupId,
  albumName,
  items,
  canUseOriginals,
  onClose,
}: SaveToPhoneProps) {
  const { toast } = useToast();
  const strategy = useMemo(saveStrategy, []);
  const apple = useMemo(isApplePhone, []);

  /*
   * Decrypted copies this dialog is holding.
   *
   * A save of two hundred photographs opens two hundred of them, and every one
   * stays in memory until it is given back. Released when the dialog closes,
   * which is also when the share sheet is finished with them.
   */
  const opened = useRef(new Set<string>());
  useEffect(() => {
    const held = opened.current;
    return () => {
      for (const url of held) release(url);
      held.clear();
    };
  }, []);

  const [phase, setPhase] = useState<Phase>("idle");
  const [batchIndex, setBatchIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const batches = useMemo(() => batchForSharing(items), [items]);
  const totalBytes = items.reduce((sum, item) => sum + item.sizeBytes, 0);

  const resolveUrls = useCallback(
    async (targets: SaveTarget[]): Promise<DownloadableFile[]> => {
      // Chunked: a selection has no upper bound, and these ids travel in the
      // query string. Saving forty photographs used to build a URL nothing
      // would carry.
      const urls = await fetchSignedUrls(
        groupId,
        targets.map((item) => item.id),
        canUseOriginals ? "original" : "preview_hq",
        await imageAccept(),
      );
      const result = { urls };

      /*
       * Opened before they are handed on. What the share sheet or the download
       * receives has to be the photograph — saving ciphertext to somebody's
       * camera roll would produce a folder of files nothing can display.
       */
      const byId = new Map(
        result.urls.map((entry) => [entry.fileId as string, entry]),
      );
      const resolved = await Promise.all(
        targets.map(async (item) => {
          const entry = byId.get(item.id);
          if (!entry?.url) return null;

          const url = await acquireDecryptedUrl({
            url: entry.url,
            wrappedFileKey: entry.wrappedFileKey ?? null,
            groupId,
            contentType: "image/webp",
          }).catch(() => null);
          if (!url) return null;

          opened.current.add(url);
          return { fileId: item.id, name: item.name, url };
        }),
      );

      return resolved.filter(
        (entry): entry is DownloadableFile => entry !== null,
      );
    },
    [groupId, canUseOriginals],
  );

  /*
   * Share must be called straight from the tap, with no `await` before it, or
   * iOS treats the gesture as expired and silently refuses. So the bytes for
   * this batch are fetched first, and only then is `share` invoked — which is
   * why this is two steps rather than one button.
   */
  const shareBatch = useCallback(async () => {
    const batch = batches[batchIndex];
    if (!batch) return;

    setPhase("working");
    setProgress(0);

    try {
      const urls = await resolveUrls(batch);
      const files = await fetchAsFiles(urls, (done, total) =>
        setProgress(done / total),
      );

      const outcome = await shareFiles(files);
      if (outcome === "unsupported" || outcome === "failed") {
        toast("Your browser wouldn't open the share sheet.", {
          variant: "danger",
        });
        setPhase("idle");
        return;
      }
      if (outcome === "cancelled") {
        setPhase("idle");
        return;
      }

      const next = batchIndex + 1;
      setBatchIndex(next);
      setPhase(next >= batches.length ? "done" : "idle");
    } catch {
      toast("Couldn't prepare those photos.", { variant: "danger" });
      setPhase("idle");
    }
  }, [batches, batchIndex, resolveUrls, toast]);

  const runDownload = useCallback(async () => {
    setPhase("working");
    setProgress(0);

    try {
      const urls = await resolveUrls(items);
      const saved = await downloadFiles(urls, (done, total) =>
        setProgress(done / total),
      );
      setPhase("done");
      if (saved < items.length) {
        toast(`Saved ${saved} of ${items.length}. Some couldn't be fetched.`, {
          variant: "danger",
        });
      }
    } catch {
      toast("Couldn't save those photos.", { variant: "danger" });
      setPhase("idle");
    }
  }, [items, resolveUrls, toast]);

  const remaining = items.length - batchIndex * SHARE_MAX_FILES;

  return (
    <Dialog
      open
      title="Save to your phone"
      onClose={onClose}
      footer={
        <div className="fc-save__actions">
          <Button variant="ghost" onClick={onClose}>
            {phase === "done" ? "Close" : "Cancel"}
          </Button>

          {strategy === "share" && phase !== "done" && (
            <Button
              variant="primary"
              loading={phase === "working"}
              onClick={() => void shareBatch()}
            >
              {batches.length > 1
                ? `Save next ${String(Math.min(SHARE_MAX_FILES, remaining))}`
                : "Save to Photos"}
            </Button>
          )}

          {strategy === "download" && phase !== "done" && (
            <Button
              variant="primary"
              loading={phase === "working"}
              onClick={() => void runDownload()}
            >
              Save {items.length}
            </Button>
          )}
        </div>
      }
    >
      <div className="fc-save">
        <p className="fc-save__summary">
          {items.length} {items.length === 1 ? "photo" : "photos"} from{" "}
          <strong>{albumName}</strong> · {formatBytes(totalBytes)}
        </p>

        {strategy === "share" && (
          <>
            <p className="fc-save__body">
              This opens your phone&apos;s share sheet. Choose{" "}
              <strong>{apple ? "Save Image" : "your gallery app"}</strong> there
              to put them in your photos.
            </p>
            {batches.length > 1 && (
              <p className="fc-save__note">
                Phones only accept about {SHARE_MAX_FILES} at a time, so this
                goes in {batches.length} rounds. You&apos;ll tap once per round.
              </p>
            )}
          </>
        )}

        {strategy === "download" && (
          <p className="fc-save__body">
            These save to your <strong>Downloads</strong> folder. Your gallery
            app picks them up from there — look for a <strong>Download</strong>{" "}
            album.
          </p>
        )}

        {strategy === "longpress" && (
          <>
            <p className="fc-save__body">
              This browser can&apos;t hand photos to your camera roll directly.
            </p>
            <p className="fc-save__note">
              Open a photo, then <strong>touch and hold it</strong> and choose{" "}
              <strong>Add to Photos</strong>. Opening this page in Safari gives
              you a &ldquo;save all&rdquo; option instead.
            </p>
          </>
        )}

        {phase === "working" && (
          <ProgressBar
            value={progress}
            label={strategy === "share" ? "Preparing photos" : "Saving photos"}
          />
        )}

        {phase === "done" && (
          <p className="fc-save__done">
            {/* Deliberately not "Saved!" — the share sheet never reports which
                target the user picked, so claiming success would be a guess. */}
            {strategy === "share"
              ? "That's all of them handed over."
              : "Finished. Check your gallery's Download album."}
          </p>
        )}
      </div>
    </Dialog>
  );
}
